import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { X, Send, Loader2 } from "lucide-react";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Hide Lex on private routes (chat + admin)
const HIDDEN_ROUTES = ["/chat", "/admin"];

const LS_KEY = "prelegal_lex_session_v1";

// Build a stable session_id per device + persist it locally
const getSessionId = () => {
  try {
    let sid = window.localStorage.getItem(LS_KEY);
    if (!sid) {
      // 16-char hex, no deps
      sid = "lex_" + Array.from(crypto.getRandomValues(new Uint8Array(12)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      window.localStorage.setItem(LS_KEY, sid);
    }
    return sid;
  } catch {
    return "lex_" + Math.random().toString(36).slice(2);
  }
};

const GREETING = {
  role: "assistant",
  content:
    "¡Hola! 👋 Soy Lex, asistente de Pre-Legal.cl. ¿En qué te puedo ayudar? Puedo contarte cómo funciona, qué planes hay, o lo que quieras saber del servicio.",
};

const SUGGESTIONS = [
  "¿Qué es Pre-Legal?",
  "¿Cuánto cuesta?",
  "¿Cómo funciona?",
];

export default function LexWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionId] = useState(getSessionId);
  const [hasOpened, setHasOpened] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Hide on private routes
  const hidden = HIDDEN_ROUTES.some((r) => pathname.startsWith(r));

  // Fetch prior history once on first open, so the user picks up where they left off
  useEffect(() => {
    if (!open || hasOpened) return;
    setHasOpened(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/lex/history`, {
          params: { session_id: sessionId, _ts: Date.now() },
        });
        if (Array.isArray(data?.messages) && data.messages.length > 0) {
          setMessages([GREETING, ...data.messages.map((m) => ({ role: m.role, content: m.content }))]);
        }
      } catch {
        /* silent: fresh greeting still works */
      }
    })();
  }, [open, hasOpened, sessionId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const send = useCallback(
    async (text) => {
      const msg = (text ?? input).trim();
      if (!msg || loading) return;
      setInput("");
      setError("");
      setMessages((prev) => [...prev, { role: "user", content: msg }]);
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/lex/chat`, {
          session_id: sessionId,
          message: msg,
        });
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data?.reply || "…" },
        ]);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setError(
          typeof detail === "string"
            ? detail
            : "No pude responder ahora. Intenta en unos segundos."
        );
      } finally {
        setLoading(false);
      }
    },
    [input, loading, sessionId]
  );

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Inline animation + styles scoped to Lex */}
      <style>{`
        @keyframes lexHaloPulse {
          0%   { transform: scale(1);   opacity: 0.55; }
          50%  { transform: scale(1.45); opacity: 0; }
          100% { transform: scale(1);   opacity: 0;  }
        }
        @keyframes lexHaloRotate {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes lexBubbleIn {
          from { transform: translateY(6px); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
        @keyframes lexDot {
          0%, 80%, 100% { opacity: 0.25; }
          40%           { opacity: 1;    }
        }
        @keyframes lexOnlinePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.55); }
          50%      { box-shadow: 0 0 0 5px rgba(34,197,94,0);   }
        }
        .lex-halo-ring {
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          pointer-events: none;
        }
      `}</style>

      {/* Floating launcher — visible when panel closed */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Abrir asistente Lex"
          data-testid="lex-launcher"
          style={{
            position: "fixed",
            right: "clamp(12px, 2vw, 24px)",
            bottom: "clamp(12px, 2vw, 24px)",
            width: 64,
            height: 64,
            borderRadius: 9999,
            border: "none",
            padding: 0,
            cursor: "pointer",
            background: "transparent",
            zIndex: 9998,
            transition: "transform 0.18s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.06)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          {/* Animated halo rings (tricolor) — always on, even after first open */}
          <span
            className="lex-halo-ring"
            style={{
              background: "conic-gradient(from 0deg, #1a3a8f 0%, #ffffff 33%, #d52b1e 66%, #1a3a8f 100%)",
              animation: "lexHaloPulse 2.2s ease-out infinite",
            }}
          />
          <span
            className="lex-halo-ring"
            style={{
              background: "conic-gradient(from 120deg, #1a3a8f 0%, #ffffff 33%, #d52b1e 66%, #1a3a8f 100%)",
              animation: "lexHaloPulse 2.2s ease-out infinite 0.75s",
            }}
          />
          {/* Rotating tricolor ring behind the button */}
          <span
            className="lex-halo-ring"
            aria-hidden="true"
            style={{
              background: "conic-gradient(from 0deg, #1a3a8f, #ffffff, #d52b1e, #1a3a8f)",
              animation: "lexHaloRotate 6s linear infinite",
              filter: "blur(2px)",
            }}
          />
          {/* Main solid button — white core with the Pre-Legal column logo */}
          <span
            style={{
              position: "absolute",
              inset: 3,
              borderRadius: 9999,
              overflow: "hidden",
              background: "#ffffff",
              boxShadow:
                "0 8px 24px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="/logo-prelegal.png"
              alt="Pre-Legal.cl"
              width={38}
              height={38}
              style={{
                width: 38,
                height: 38,
                objectFit: "contain",
                display: "block",
              }}
            />
          </span>
          {/* "Online" green dot — mirrors the one in the open panel header */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 3,
              right: 3,
              width: 14,
              height: 14,
              borderRadius: 9999,
              background: "#22c55e",
              border: "2.5px solid #ffffff",
              zIndex: 2,
              animation: "lexOnlinePulse 1.9s ease-out infinite",
            }}
          />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="lex-panel"
          role="dialog"
          aria-label="Asistente Lex"
          style={{
            position: "fixed",
            right: "clamp(12px, 2vw, 24px)",
            bottom: "clamp(12px, 2vw, 24px)",
            width: "min(380px, calc(100vw - 24px))",
            height: "min(560px, calc(100vh - 40px))",
            background: "#ffffff",
            borderRadius: 18,
            boxShadow:
              "0 28px 60px -12px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.06)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 9998,
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
            animation: "lexBubbleIn 180ms ease-out",
          }}
        >
          {/* Header — light blue gradient */}
          <div
            style={{
              background:
                "linear-gradient(135deg, #1a3a8f 0%, #2563eb 55%, #1a3a8f 100%)",
              color: "#ffffff",
              padding: "14px 18px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              position: "relative",
            }}
          >
            {/* Avatar — official Pre-Legal column logo */}
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9999,
                background: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
                overflow: "hidden",
              }}
            >
              <img
                src="/logo-prelegal.png"
                alt="Pre-Legal.cl"
                width={26}
                height={26}
                style={{ width: 26, height: 26, objectFit: "contain" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: "-0.01em" }}>Lex</div>
              <div style={{ fontSize: 11, opacity: 0.92, display: "flex", alignItems: "center", gap: 7 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 9999,
                    background: "#22c55e",
                    display: "inline-block",
                    animation: "lexOnlinePulse 1.9s ease-out infinite",
                  }}
                />
                Asistente virtual · En línea
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cerrar"
              data-testid="lex-close"
              style={{
                background: "rgba(255,255,255,0.14)",
                border: "none",
                color: "#fff",
                borderRadius: 9999,
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.28)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.14)")}
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages area */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "18px 16px",
              background:
                "linear-gradient(180deg, #f5fbff 0%, #f0f9f6 100%)",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {messages.map((m, i) => (
              <Bubble key={i} msg={m} />
            ))}
            {loading && <TypingBubble />}
            {error && (
              <div
                role="alert"
                data-testid="lex-error"
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  color: "#991b1b",
                  borderRadius: 10,
                  padding: "8px 10px",
                  fontSize: 12,
                  alignSelf: "center",
                }}
              >
                {error}
              </div>
            )}

            {/* Quick-reply chips (only on the initial greeting) */}
            {messages.length === 1 && !loading && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    data-testid={`lex-suggestion-${s.length}`}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 9999,
                      border: "1px solid rgba(37, 99, 235, 0.3)",
                      background: "rgba(37, 99, 235, 0.08)",
                      color: "#1e3a8a",
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(37, 99, 235, 0.16)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(37, 99, 235, 0.08)";
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input area */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            style={{
              borderTop: "1px solid #e2e8f0",
              padding: 12,
              background: "#ffffff",
              display: "flex",
              alignItems: "flex-end",
              gap: 8,
            }}
            data-testid="lex-form"
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Escribe tu pregunta…"
              rows={1}
              maxLength={1000}
              data-testid="lex-input"
              style={{
                flex: 1,
                resize: "none",
                border: "1px solid #cbd5e1",
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 14,
                lineHeight: 1.4,
                outline: "none",
                fontFamily: "inherit",
                color: "#0f172a",
                background: "#f8fafc",
                minHeight: 38,
                maxHeight: 120,
              }}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              aria-label="Enviar"
              data-testid="lex-send"
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                border: "none",
                background: loading || !input.trim() ? "#cbd5e1" : "#2563eb",
                color: "#fff",
                cursor: loading || !input.trim() ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "background 0.15s, box-shadow 0.15s",
                boxShadow: loading || !input.trim() ? "none" : "0 6px 14px rgba(37, 99, 235, 0.28)",
              }}
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            </button>
          </form>

          {/* Footer disclaimer */}
          <div
            style={{
              padding: "6px 12px 10px",
              fontSize: 10,
              color: "#64748b",
              textAlign: "center",
              background: "#ffffff",
            }}
          >
            Lex te orienta sobre Pre-Legal.cl · No da asesoría legal
          </div>
        </div>
      )}
    </>
  );
}

function Bubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div
      style={{
        maxWidth: "82%",
        alignSelf: isUser ? "flex-end" : "flex-start",
        background: isUser ? "#2563eb" : "#ffffff",
        color: isUser ? "#ffffff" : "#0f172a",
        padding: "9px 12px",
        borderRadius: 14,
        borderBottomRightRadius: isUser ? 4 : 14,
        borderBottomLeftRadius: isUser ? 14 : 4,
        fontSize: 13,
        lineHeight: 1.5,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        border: isUser ? "1px solid rgba(37,99,235,0.35)" : "1px solid #e2e8f0",
        boxShadow: isUser
          ? "0 4px 10px rgba(37,99,235,0.22)"
          : "0 2px 6px rgba(15,23,42,0.04)",
        animation: "lexBubbleIn 140ms ease-out",
      }}
    >
      {msg.content}
    </div>
  );
}

function TypingBubble() {
  return (
    <div
      data-testid="lex-typing"
      style={{
        alignSelf: "flex-start",
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        borderBottomLeftRadius: 4,
        padding: "10px 14px",
        display: "inline-flex",
        gap: 5,
      }}
    >
      {[0, 0.15, 0.3].map((d, i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: 9999,
            background: "#2563eb",
            animation: "lexDot 1.2s infinite both",
            animationDelay: `${d}s`,
            display: "inline-block",
          }}
        />
      ))}
    </div>
  );
}
